import type { ReactNode, ComponentType } from 'react'

// Accent por portal. Default 'purple' mantém o visual atual de todas as páginas;
// portais com identidade própria (cp=blue, rs=indigo) podem sobrescrever.
// Cores canônicas por tipo de org (ver orgTypeColors em OrgSwitcher):
// end_customer=purple, cloud_provider=blue, reseller=emerald.
export type HeaderAccent = 'purple' | 'blue' | 'emerald'

const ICON_ACCENT: Record<HeaderAccent, string> = {
  purple: 'text-purple-600',
  blue: 'text-blue-600',
  emerald: 'text-emerald-600',
}

type ResponsivePageHeaderProps = {
  title: string
  description?: string
  icon?: ComponentType<{ className?: string }>
  meta?: ReactNode
  actions?: ReactNode
  className?: string
  accent?: HeaderAccent
}

/**
 * Responsive page header helper.
 * Usage:
 * - Keep title/description on the left.
 * - Pass primary/secondary actions via `actions`; they stack on mobile.
 */
export function ResponsivePageHeader({
  title,
  description,
  icon: Icon,
  meta,
  actions,
  className = '',
  accent = 'purple',
}: ResponsivePageHeaderProps) {
  return (
    <header className={`mb-6 ${className}`}>
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 sm:text-3xl">
            {Icon ? <Icon className={`h-7 w-7 ${ICON_ACCENT[accent]} sm:h-8 sm:w-8`} /> : null}
            <span className="truncate">{title}</span>
          </h1>
          {description ? (
            <p className="mt-2 text-sm text-gray-600 sm:text-base">{description}</p>
          ) : null}
          {meta ? <div className="mt-2">{meta}</div> : null}
        </div>

        {actions ? (
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end">
            {actions}
          </div>
        ) : null}
      </div>
    </header>
  )
}
